import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { showsAPI } from '../lib/api';
import { computeCueNumbers } from '../lib/cueNumbering';
import { computeInTimes } from '../lib/rundownDerive';
import { useAuthStore } from '../stores/authStore';

// "Workspace - Show Name", dropping either half when it is missing.
function joinTitle(workspaceName, showName, fallback) {
  const parts = [workspaceName, showName]
    .map(v => String(v || '').replace(/\s+/g, ' ').trim())
    .filter(Boolean);
  return parts.join(' - ') || fallback;
}

// Heading shown on the page. Keeps punctuation the filename version has to drop.
function buildDisplayTitle(workspaceName, showName) {
  return joinTitle(workspaceName, showName, 'Untitled Show');
}

// The filename the print dialog offers when saving to PDF. Characters that are
// illegal in Windows/macOS filenames are folded to a space up front, otherwise
// the browser silently swaps them for underscores.
function buildPrintTitle(workspaceName, showName) {
  const clean = v => String(v || '').replace(/[\\/:*?"<>|]/g, ' ');
  return joinTitle(clean(workspaceName), clean(showName), 'Rundown');
}

function formatDuration(seconds) {
  if (seconds == null || seconds === '') return '';
  const s = Number(seconds);
  if (isNaN(s)) return '';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function formatClockTime(date) {
  if (!date) return '';
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');
  return `${h}:${m}:${s}`;
}

function formatLongDate(date) {
  if (!date) return '';
  return date.toLocaleDateString(undefined, {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
}


export default function PrintRundown() {
  const { id: showId } = useParams();
  const [show, setShow] = useState(null);
  const [cues, setCues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const workspaceName = useAuthStore(s => s.user?.workspace_name);

  useEffect(() => {
    showsAPI.get(showId)
      .then(res => {
        setShow(res.data);
        const sorted = [...(res.data.cues || [])].sort((a, b) => a.order - b.order);
        setCues(sorted);
      })
      .catch(err => setError(err.response?.data?.error || 'Failed to load show'))
      .finally(() => setLoading(false));
  }, [showId]);

  // The browser's print dialog seeds the PDF filename from document.title, so
  // this is what names the saved file: "Workspace - Show Name".
  useEffect(() => {
    document.title = buildPrintTitle(workspaceName, show?.name);
  }, [workspaceName, show?.name]);

  const inTimes = useMemo(() => computeInTimes(cues, show), [cues, show]);
  const cueNumbers = useMemo(() => computeCueNumbers(cues), [cues]);

  const totalSeconds = useMemo(
    () => cues.reduce((sum, c) => sum + (c.planned_duration || 0), 0),
    [cues]
  );

  function formatTotal(secs) {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
    return `${m}m ${String(s).padStart(2, '0')}s`;
  }

  const generatedAt = new Date();
  const scheduledStart = show?.scheduled_start ? new Date(show.scheduled_start) : null;

  // Derived from the last cue's out time so it always matches the Time Out column.
  // Falls back to scheduled start + total duration when cues have no clock times.
  const estimatedEnd = useMemo(() => {
    if (!cues.length) return null;
    const lastIn = inTimes[inTimes.length - 1];
    if (lastIn) {
      const lastDuration = (cues[cues.length - 1]?.planned_duration || 0) * 1000;
      return new Date(lastIn.getTime() + lastDuration);
    }
    if (scheduledStart) return new Date(scheduledStart.getTime() + totalSeconds * 1000);
    return null;
  }, [cues, inTimes, scheduledStart, totalSeconds]);

  // Late-night shows can run past midnight; a bare clock time would hide that.
  const endsNextDay = useMemo(() => {
    const firstIn = inTimes.find(Boolean);
    if (!estimatedEnd || !firstIn) return false;
    return estimatedEnd.toDateString() !== firstIn.toDateString();
  }, [estimatedEnd, inTimes]);

  if (loading) {
    return <div className="p-8 text-gray-700">Loading rundown…</div>;
  }

  if (error) {
    return <div className="p-8 text-red-700">{error}</div>;
  }

  return (
    <div className="print-root">
      <style>{`
        .print-root {
          background: white;
          color: black;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          font-size: 11px;
          padding: 16px 24px 32px;
          max-width: 1500px;
          margin: 0 auto;
          min-height: 100vh;
        }
        .toolbar {
          position: sticky;
          top: 0;
          background: white;
          padding: 8px 0 12px;
          border-bottom: 1px solid #ccc;
          margin-bottom: 16px;
          display: flex;
          gap: 8px;
          align-items: center;
          z-index: 10;
        }
        .toolbar button {
          padding: 6px 14px;
          font-size: 13px;
          font-weight: 600;
          background: #1a1a1a;
          color: white;
          border: 0;
          border-radius: 4px;
          cursor: pointer;
        }
        .toolbar button:hover { background: #333; }
        .toolbar .hint { color: #666; font-size: 12px; }

        .show-header { margin-bottom: 16px; }
        .show-header h1 {
          font-size: 22px;
          font-weight: 700;
          margin: 0 0 6px;
          line-height: 1.2;
        }
        .show-meta {
          display: flex;
          gap: 18px;
          flex-wrap: wrap;
          font-size: 12px;
          color: #333;
        }
        .show-meta b { color: #000; font-weight: 600; }

        table.rundown {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
        }
        table.rundown th {
          text-align: left;
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          padding: 6px 6px;
          background: #f0f0f0;
          border-bottom: 2px solid #333;
          border-right: 1px solid #ccc;
          color: #000;
        }
        table.rundown td {
          padding: 6px 6px;
          vertical-align: top;
          border-bottom: 1px solid #ddd;
          border-right: 1px solid #e5e5e5;
          font-size: 11px;
          color: #111;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        table.rundown th:last-child,
        table.rundown td:last-child { border-right: 0; }
        table.rundown tr.row-onair td { background: #fff2f2; }
        table.rundown tr.row-complete td { color: #888; }
        table.rundown tr.row-skipped td { color: #999; text-decoration: line-through; }
        table.rundown tr.row-group td {
          background: #efefef;
          font-weight: 700;
          border-top: 1px solid #999;
        }
        .type-badge {
          display: inline-block;
          padding: 1px 5px;
          border-radius: 3px;
          font-size: 9px;
          font-weight: 700;
          letter-spacing: 0.04em;
          border: 1px solid #999;
          color: #000;
          background: #fff;
          white-space: nowrap;
        }
        .col-order {
          width: 46px;
          text-align: right;
          color: #666;
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
        }
        .col-label { width: auto; }
        .col-type { width: 88px; }
        .col-duration { width: 60px; font-variant-numeric: tabular-nums; }
        .col-time-in, .col-time-out { width: 70px; font-variant-numeric: tabular-nums; }
        .col-script { width: 30%; }
        .col-notes { width: 22%; }
        .script-text, .notes-text { font-size: 11px; line-height: 1.35; }
        .notes-text { white-space: pre-wrap; color: #444; font-style: italic; }

        /* Rich text from the TipTap script editor, restyled for print */
        .script-text p { margin: 0 0 0.4em; }
        .script-text > :last-child { margin-bottom: 0; }
        .script-text h1 { font-size: 1.3em; font-weight: 700; margin: 0 0 0.3em; }
        .script-text h2 { font-size: 1.1em; font-weight: 600; margin: 0 0 0.3em; }
        .script-text strong { font-weight: 700; }
        .script-text em { font-style: italic; }
        .script-text u { text-decoration: underline; }
        .script-text ul { list-style: disc; padding-left: 1.2em; margin: 0 0 0.4em; }
        .script-text ol { list-style: decimal; padding-left: 1.2em; margin: 0 0 0.4em; }
        .script-text li { margin-bottom: 0.1em; }
        .script-text li p { margin: 0; }
        .footer-meta {
          margin-top: 18px;
          font-size: 10px;
          color: #777;
          border-top: 1px solid #ddd;
          padding-top: 8px;
        }

        @media print {
          .toolbar { display: none !important; }
          .print-root { padding: 0; max-width: 100%; }
          @page { size: A4 landscape; margin: 10mm 12mm; }
          table.rundown { font-size: 10px; }
          table.rundown thead { display: table-header-group; }
          table.rundown tr { page-break-inside: avoid; }
          table.rundown tr.row-group { page-break-after: avoid; }
          table.rundown tr.row-onair td { background: #f5f5f5 !important; }
        }
      `}</style>

      <div className="toolbar">
        <button onClick={() => window.print()}>Print / Save as PDF</button>
        <button onClick={() => window.close()} style={{ background: '#666' }}>Close</button>
        <span className="hint">Use your browser's print dialog (Cmd/Ctrl+P) to print or save as PDF.</span>
      </div>

      <div className="show-header">
        <h1>{buildDisplayTitle(workspaceName, show?.name)}</h1>
        <div className="show-meta">
          {show?.sport_type && show.sport_type !== 'general' && (
            <span><b>Sport:</b> {show.sport_type}</span>
          )}
          {scheduledStart && (
            <span><b>Date:</b> {formatLongDate(scheduledStart)}</span>
          )}
          {scheduledStart && (
            <span><b>Scheduled Start:</b> {formatClockTime(scheduledStart)}</span>
          )}
          <span><b>Cues:</b> {cues.length}</span>
          <span><b>Total Duration:</b> {formatTotal(totalSeconds)}</span>
          {estimatedEnd && (
            <span>
              <b>Estimated End:</b> {formatClockTime(estimatedEnd)}
              {endsNextDay ? ' (+1 day)' : ''}
            </span>
          )}
          {show?.status && (
            <span><b>Status:</b> {String(show.status).toUpperCase()}</span>
          )}
        </div>
      </div>

      <table className="rundown">
        <colgroup>
          <col className="col-order" />
          <col className="col-label" />
          <col className="col-type" />
          <col className="col-duration" />
          <col className="col-time-in" />
          <col className="col-time-out" />
          <col className="col-script" />
          <col className="col-notes" />
        </colgroup>
        <thead>
          <tr>
            <th className="col-order">#</th>
            <th className="col-label">Label</th>
            <th className="col-type">Type</th>
            <th className="col-duration">Dur</th>
            <th className="col-time-in">Time In</th>
            <th className="col-time-out">Time Out</th>
            <th className="col-script">Script</th>
            <th className="col-notes">Notes</th>
          </tr>
        </thead>
        <tbody>
          {cues.map((cue, idx) => {
            const timeIn = inTimes[idx] || null;
            const timeOut = timeIn
              ? new Date(timeIn.getTime() + (cue.planned_duration || 0) * 1000)
              : null;
            const rowClass =
              cue.cue_type === 'GROUP' ? 'row-group' :
              cue.status === 'on_air' ? 'row-onair' :
              cue.status === 'complete' ? 'row-complete' :
              cue.status === 'skipped' ? 'row-skipped' : '';
            return (
              <tr key={cue.id} className={rowClass}>
                <td className="col-order">{cueNumbers.get(cue.id) ?? idx + 1}</td>
                <td className="col-label">{cue.label || ''}</td>
                <td className="col-type">
                  <span className="type-badge">{cue.cue_type}</span>
                </td>
                <td className="col-duration">{formatDuration(cue.planned_duration)}</td>
                <td className="col-time-in">{formatClockTime(timeIn)}</td>
                <td className="col-time-out">{formatClockTime(timeOut)}</td>
                <td className="col-script">
                  {cue.script
                    ? <div className="script-text" dangerouslySetInnerHTML={{ __html: cue.script }} />
                    : null}
                </td>
                <td className="col-notes"><div className="notes-text">{cue.notes || ''}</div></td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="footer-meta">
        Generated {generatedAt.toLocaleString()} · LiveRundown
      </div>
    </div>
  );
}
